import React, { useEffect, useState, useRef } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useLocation,
} from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Home from './pages/Home';
import Contact from './pages/Contact';
import MS4Nature from './pages/MS4Nature';
import MS4Materials from './pages/MS4Materials';
import About from './pages/About';
import LegalNotice from './pages/LegalNotice';
import NotFound from './pages/NotFound';

function ScrollToTop() {
  const location = useLocation();

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'instant',
      });
    }, 0);
  }, [location.pathname]);

  return null;
}

function NavigationContent() {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const [isCompanyMenuOpen, setIsCompanyMenuOpen] = useState(false);
  const languageMenuRef = useRef<HTMLDivElement>(null);
  const companyMenuRef = useRef<HTMLDivElement>(null);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    console.log('App component mounted');
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        languageMenuRef.current &&
        !languageMenuRef.current.contains(event.target as Node)
      ) {
        setIsLanguageMenuOpen(false);
      }
      if (
        companyMenuRef.current &&
        !companyMenuRef.current.contains(event.target as Node)
      ) {
        setIsCompanyMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const changeLanguage = (lang: 'en' | 'fr') => {
    i18n.changeLanguage(lang);
    setIsLanguageMenuOpen(false);
  };

  const languages = [
    { code: 'fr', flagIcon: '/icons/fr.svg', name: 'Français' },
    { code: 'en', flagIcon: '/icons/gb.svg', name: 'English' },
  ];

  const currentLanguage =
    languages.find((lang) => lang.code === i18n.language) || languages[0];

  const getLoginUrl = () => {
    const hostname = window.location.hostname;
    const rawLang = i18n.language || 'fr';
    const baseLang = rawLang.toLowerCase().split(/[-_]/)[0];
    const lang = baseLang === 'en' ? 'en' : 'fr';
    const callbackUrl = encodeURIComponent(
      `https://${hostname}/platform/${lang}`
    );
    return `https://${hostname}/platform/${lang}/login?callbackUrl=${callbackUrl}`;
  };

  return (
    <>
      {/* Navigation */}
      <nav className="fixed z-50 w-full bg-white shadow-sm">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 justify-between">
            <div className="flex w-full items-center justify-start md:w-auto md:justify-start">
              <Link to="/">
                <img
                  src="/logos/ms4all_logo.svg"
                  alt="Logo MS4ALL"
                  className="h-6 w-fit sm:h-8"
                  width={120}
                  height={32}
                />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden items-center space-x-8 md:flex">
              <Link
                to="/ms4nature"
                className="rounded-md px-3 py-2 text-sm font-medium text-gray-600 hover:text-[#006AB5]"
              >
                {t('nav.ms4nature')}
              </Link>
              <Link
                to="/ms4materials"
                className="rounded-md px-3 py-2 text-sm font-medium text-gray-600 hover:text-[#006AB5]"
              >
                {t('nav.ms4materials')}
              </Link>
              <a
                href="/platform"
                className="rounded-md px-3 py-2 text-sm font-medium text-gray-600 hover:text-[#006AB5]"
              >
                {t('nav.ourProduct')}
              </a>
              <div className="relative" ref={companyMenuRef}>
                <button
                  onClick={() => setIsCompanyMenuOpen(!isCompanyMenuOpen)}
                  className="flex items-center space-x-1 rounded-md px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:text-[#006AB5]"
                  aria-label={
                    isCompanyMenuOpen
                      ? 'Close company menu'
                      : 'Open company menu'
                  }
                >
                  <span>{t('nav.ourCompany')}</span>
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${
                      isCompanyMenuOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {isCompanyMenuOpen && (
                  <div className="absolute left-0 z-50 mt-2 w-40 rounded-md border border-gray-200 bg-white py-1 shadow-lg">
                    <Link
                      to="/contact"
                      onClick={() => setIsCompanyMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-100"
                    >
                      {t('nav.contact')}
                    </Link>
                    <Link
                      to="/about"
                      onClick={() => setIsCompanyMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-100"
                    >
                      {t('nav.about')}
                    </Link>
                  </div>
                )}
              </div>
              <div className="relative" ref={languageMenuRef}>
                <button
                  onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                  className="flex items-center space-x-1 rounded-md px-2 py-2 text-sm font-medium text-gray-600 transition-colors hover:text-[#006AB5]"
                  title="Change language"
                  aria-label="Change language"
                >
                  <img
                    src={currentLanguage.flagIcon}
                    alt=""
                    className="h-5 w-5 rounded-sm"
                    aria-hidden="true"
                  />
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${
                      isLanguageMenuOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {isLanguageMenuOpen && (
                  <div className="absolute right-0 z-50 mt-2 w-40 rounded-md border border-gray-200 bg-white py-1 shadow-lg">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code as 'en' | 'fr')}
                        className={`flex w-full items-center space-x-2 px-4 py-2 text-sm transition-colors hover:bg-gray-100 ${
                          i18n.language === lang.code
                            ? 'bg-blue-50 text-[#006AB5]'
                            : 'text-gray-700'
                        }`}
                      >
                        <img
                          src={lang.flagIcon}
                          alt=""
                          className="h-5 w-5 rounded-sm"
                          aria-hidden="true"
                        />
                        <span>{lang.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="group relative w-fit">
                <a
                  href={getLoginUrl()}
                  className="inline-block rounded-md bg-[#006AB5] px-4 py-2 text-white hover:bg-[#005A99]"
                >
                  {t('nav.signIn')}
                </a>
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="flex items-center md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-600 hover:text-gray-900"
                aria-label={
                  isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'
                }
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden">
            <div className="space-y-1 px-2 pb-3 pt-2 sm:px-3">
              <Link
                to="/ms4nature"
                className="block rounded-md px-3 py-2 text-base font-medium text-gray-600 hover:text-[#006AB5]"
              >
                {t('nav.ms4nature')}
              </Link>
              <Link
                to="/ms4materials"
                className="block rounded-md px-3 py-2 text-base font-medium text-gray-600 hover:text-[#006AB5]"
              >
                {t('nav.ms4materials')}
              </Link>
              <a
                href="/platform"
                className="rounded-md px-3 py-2 text-sm font-medium text-gray-600 hover:text-[#006AB5]"
              >
                {t('nav.ourProduct')}
              </a>
              <div className="px-3 py-2">
                <div className="mb-1 text-sm font-semibold text-gray-600">
                  {t('nav.ourCompany')}
                </div>
                <Link
                  to="/contact"
                  className="block rounded-md px-3 py-2 text-base font-medium text-gray-600 hover:text-[#006AB5]"
                >
                  {t('nav.contact')}
                </Link>
                <Link
                  to="/about"
                  className="block rounded-md px-3 py-2 text-base font-medium text-gray-600 hover:text-[#006AB5]"
                >
                  {t('nav.about')}
                </Link>
              </div>
              <div className="space-y-1">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code as 'en' | 'fr')}
                    className={`flex w-full items-center space-x-2 rounded-md px-3 py-2 text-left text-base font-medium text-gray-600 hover:text-[#006AB5] ${
                      i18n.language === lang.code
                        ? 'bg-blue-50 text-[#006AB5]'
                        : ''
                    }`}
                  >
                    <img
                      src={lang.flagIcon}
                      alt=""
                      className="h-5 w-5 rounded-sm"
                      aria-hidden="true"
                    />
                    <span>{lang.name}</span>
                  </button>
                ))}
              </div>
              <a
                href={getLoginUrl()}
                className="block rounded-md bg-[#006AB5] px-3 py-2 text-center text-white hover:bg-[#005A99]"
              >
                {t('nav.signIn')}
              </a>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}

function App() {
  const { i18n } = useTranslation();

  useEffect(() => {
    const applyLang = (lng: string) => {
      const baseLang = (lng || 'fr').toLowerCase().split(/[-_]/)[0];
      document.documentElement.lang = baseLang === 'en' ? 'en' : 'fr';
    };

    applyLang(i18n.language);
    i18n.on('languageChanged', applyLang);
    return () => {
      i18n.off('languageChanged', applyLang);
    };
  }, [i18n]);

  const routes = [
    { path: '/', element: <Home /> },
    { path: '/ms4materials', element: <MS4Materials /> },
    { path: '/ms4nature', element: <MS4Nature /> },
    { path: '/contact', element: <Contact /> },
    { path: '/about', element: <About /> },
    { path: '/legal-notice', element: <LegalNotice /> },
    { path: '*', element: <NotFound /> },
  ];
  return (
    <Router>
      <ScrollToTop />
      <div className="min-h-screen bg-gray-50">
        <NavigationContent />
        <main className="pt-16">
          <Routes>
            {routes.map(({ path, element }) => (
              <Route key={path} path={path} element={element} />
            ))}
          </Routes>
        </main>
      </div>
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
      />
    </Router>
  );
}

export default App;
